import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import CheckBox from '../CheckBox/CheckBox';
import ClearBtn from 'src/Components/ClearBtn/ClearBtn';
import { EnumItemType, EnumValueType, EnumsType } from '../';
import { getFullOffsetBy, getScrollContainer } from 'src/Utils';
import { ReactComponent as ArrowIcon } from './assets/arrow.svg';
import './simpleSelect.scss';

interface SimpleSelectProps extends React.AreaHTMLAttributes<HTMLDivElement> {
	name?: string;
	placeholder?: string;
	disabled?: boolean;
	searchEnable?: boolean;
	allowNew?: boolean;

	value?: EnumValueType | EnumValueType[] | null;
	setValue: (value: string | string[], isNew?: boolean) => void;

	minSearchLength?: number;
	debouncedDelay?: number;
	onSearch?: (tern: string) => void;

	onClose?: () => void;

	options?: EnumsType;
	multiple?: boolean;
	isWait?: boolean;
	defaultOpen?: boolean;
	autoFocus?: boolean;
	showClearBtn?: boolean;
	rtl?: boolean;
	isOwnerList?: boolean;

	loadOnOpen?: boolean;
}

/** Тип позиции ( top, left ) */
export type PositionType = {
	top: number;
	left: number;
	width: number;
} | null;

const SimpleSelect: React.FC<SimpleSelectProps> = (props) => {
	const {
		name,
		allowNew = false,
		searchEnable = false,
		onSearch,
		defaultOpen = false,
		rtl = false,
		disabled = false,
		placeholder,
		showClearBtn = false,
		className,
		isOwnerList = false,
		setValue,
		value,
		options = [],
		multiple = false,
		loadOnOpen = false,
		isWait = false,
		autoFocus = false,
		debouncedDelay = 50,
		onClose,
		...other
	} = props;

	const getTitleValue = (forPlaceHolder = false): string => {
		if ((value === null || value === undefined) && forPlaceHolder) {
			return placeholder || '';
		} else if (typeof options[0] === 'object') {
			const opt = options.find((o: any) => {
				return o.id === value;
			}) as any;
			if (opt) {
				return opt.title;
			}
			return '';
		} else {
			return value?.toString() || '';
		}
	};

	const wrapperRef = useRef<HTMLDivElement>(null);
	const modalRef = useRef<HTMLDivElement>(null);
	const timerRef = useRef<NodeJS.Timeout>();
	const [position, setPosition] = useState<PositionType>(null);
	const [isOpen, setIsOpen] = useState<boolean>(!disabled && defaultOpen);
	const [textValue, setTextValue] = useState<string>(getTitleValue());
	const [searchTerm, setSearchTerm] = useState<string>('');
	const [filteredOptions, setFilteredOptions] = useState<EnumsType>([]);

	/// TODO: Вынести в хук! Написать универсалоьный контейнер
	useLayoutEffect(() => {
		const wr = wrapperRef.current;

		const calcOffsetModalPosition = () => {
			if (wr) {
				const offset = getFullOffsetBy(wr, 'rdc');
				let top = offset.top - offset.scrollTop + wr.offsetHeight;
				let left = offset.left - offset.scroll;
				const height = modalRef.current?.clientHeight || 430;

				if (top + (height + 50) > window.innerHeight) {
					wr.classList.add('upper');
					modalRef.current?.classList.add('upper');
					top -= height + offset.height;
				} else if (wrapperRef.current?.classList.contains('upper')) {
					modalRef.current?.classList.remove('upper');
					wr.classList.remove('upper');
				}

				if (left + wr.clientWidth > window.innerWidth) {
					left -= wr.clientWidth - 10;
				}
				setPosition({ top, left, width: offset.width });
			}
		};

		const onClose = () => {
			return setIsOpen(false);
		};

		if (isOpen) {
			const scrollContainer = getScrollContainer(wr);
			if (scrollContainer) {
				scrollContainer.addEventListener('scroll', onClose);
			}
		}

		calcOffsetModalPosition();

		return () => {
			if (!wr) {
				return;
			}
			const scrollContainer = getScrollContainer(wr);
			if (scrollContainer) {
				scrollContainer.removeEventListener('scroll', onClose);
			}
		};
		// eslint-disable-next-line
	}, [isOpen]);

	useEffect(() => {
		setFilteredOptions(options);
	}, [options]);

	useEffect(() => {
		/** Слушаем все клики на документе  */
		const onClick = (event: MouseEvent) => {
			const target = event.target as Node;
			if (
				!wrapperRef.current?.contains(target) &&
				!modalRef.current?.contains(target)
			) {
				!!onClose && onClose();
				setIsOpen(false);
			}
		};
		document.addEventListener('click', onClick, true);
		return () => {
			return document.removeEventListener('click', onClick, true);
		};
		// eslint-disable-next-line
	}, []);

	useEffect(() => {
		if (searchEnable) {
			if (onSearch) {
				if (timerRef.current) {
					clearTimeout(timerRef.current);
				}
				timerRef.current = setTimeout(() => {
					return onSearch(searchTerm);
				}, debouncedDelay);
			} else {
				const term = searchTerm.trim().toLowerCase();
				if (!term) {
					setFilteredOptions(options);
				} else {
					setFilteredOptions(
						options.filter((item: any) => {
							if (item.title) {
								return (
									item.title.toLowerCase().indexOf(term) !==
									-1
								);
							} else {
								return (
									item
										.toString()
										.toLowerCase()
										.indexOf(term) !== -1
								);
							}
						}),
					);
				}
			}
		} else {
			setFilteredOptions(options);
		}
		// eslint-disable-next-line
	}, [searchTerm]);

	const getIsChecked = (item: EnumItemType): boolean => {
		const id = typeof item !== 'object' ? item : item?.id;
		if (!!multiple && !!value && Array.isArray(value)) {
			return (value as Array<any>).includes(id);
		}
		return value === id;
	};

	const onChangeHandler = (id: string, title: string) => {
		if (multiple) {
			const valueArray = Array.isArray(value)
				? [...(value as string[])]
				: [];
			const index = valueArray.indexOf(id);
			if (index === -1) {
				valueArray.push(id);
			} else {
				valueArray.splice(index, 1);
			}
			setValue(valueArray);
		} else {
			setValue(id);
			!!onClose && onClose();
			setIsOpen(false);
		}
		setTextValue(title);
	};

	const onChangeTextHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
		const val = e.target.value;

		if (allowNew) {
			setTextValue(val);
		}
		if (searchEnable) {
			setSearchTerm(val);
			return;
		}

		if (typeof options[0] === 'object') {
			const item = options.find((o: any) => {
				return o.title === val;
			}) as any;
			if (item) {
				onChangeHandler(item.id, item.title);
			} else {
				setTimeout(() => {
					return setValue(val, true);
				}, debouncedDelay);
			}
		} else {
			if (options.includes(val)) {
				onChangeHandler(val, val);
			} else {
				setTimeout(() => {
					return setValue(val, true);
				}, debouncedDelay);
			}
		}
	};

	const onShowListChange = () => {
		const newValue = !isOpen;
		if (!disabled) {
			setIsOpen(newValue);
			if (newValue && loadOnOpen && onSearch) {
				onSearch('');
			}
		}
		if (!newValue && !!onClose) {
			onClose();
		}
	};

	const onClearHandler = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (searchEnable && isOpen) {
			setSearchTerm('');
		} else {
			setValue(multiple ? [] : '');
		}
	};

	const renderOption = (item: EnumItemType | string, index: number) => {
		let val: any, title: string;
		if (typeof item !== 'object') {
			val = item;
			title = item.toString();
		} else {
			val = item.id;
			title = item.title || item.id.toString();
		}
		const isChecked = getIsChecked(val);
		const itemClassses = classNames('rdccs__listitem', {
			'item-checked': isChecked,
		});
		return (
			<li
				key={index}
				className={itemClassses}
				id={'selectItemFor_' + name + '_val-' + (val ? val : 'empty')}
				onClick={() => {
					return onChangeHandler(val, title);
				}}
			>
				{!!multiple && <CheckBox name={val} value={isChecked} />}
				<span className="body-font" title={title}>
					{title}
				</span>
			</li>
		);
	};

	const renderPlaceholder = () => {
		const title = getTitleValue(true);
		const inputValue = isOpen ? (allowNew ? textValue : searchTerm) : title;
		if (allowNew || searchEnable) {
			return (
				<input
					value={inputValue}
					title={inputValue}
					placeholder={placeholder}
					type="text"
					autoFocus={searchEnable && autoFocus}
					className="rdccs__header__input"
					onChange={onChangeTextHandler}
				/>
			);
		} else {
			return (
				<span title={title} className="rdccs__header__placeholder">
					{title}
				</span>
			);
		}
	};

	const getStylesForList = () => {
		if (!isOwnerList) {
			return {};
		}
		const styles = {
			left: 0,
			top: 0,
			zIndex: '350',
			width: 100,
		};
		if (wrapperRef.current) {
			styles.left = wrapperRef.current.offsetLeft;
			styles.top =
				wrapperRef.current.offsetTop + wrapperRef.current.clientHeight;
			styles.width = wrapperRef.current.clientWidth;
		}
		return { ...styles, ...position };
	};

	const renderEmpty = () => {
		return <li className="msg">Ничего не найдено</li>;
	};

	const renderList = () => {
		return (
			<div
				ref={modalRef}
				className="rdccs__body"
				style={getStylesForList()}
			>
				<ul className="rdccs__body__listitems">
					{filteredOptions.length
						? filteredOptions.map(renderOption)
						: renderEmpty()}
				</ul>
			</div>
		);
	};

	return (
		<div
			{...other}
			ref={wrapperRef}
			className={classNames('rdccs_list-wrapper', className, {
				rtl: rtl,
			})}
			id={name}
		>
			<div
				className={classNames('rdccs__header', {
					opened: isOpen,
					disabled: disabled,
				})}
				onClick={onShowListChange}
			>
				<ArrowIcon height={33} width={32} onClick={onShowListChange} />
				{renderPlaceholder()}
				<div className="rdccs__header-toolbar">
					{!!isWait && <div className="spiner" />}
					{((!!showClearBtn && !!value) ||
						(isOpen && searchEnable && !!searchTerm)) && (
						<ClearBtn onClick={onClearHandler} />
					)}
				</div>
			</div>
			{isOpen &&
				(isOwnerList
					? createPortal(renderList(), document.body)
					: renderList())}
		</div>
	);
};

export default SimpleSelect;
